import styled from '@emotion/styled';

import {
  Button,
  Icon,
  Spacing,
  StyledDiv,
  Typography,
} from '@taleemabad/dsm/components';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';

interface LessonDetail {
  icon: string;
  label: string;
}

interface LessonCompletionProps {
  title: string;
  description: string;
  reviewTitle: string;
  reviewDescription: string;
  primaryButtonText: string;
  onPrimaryClick: () => void;
  secondaryButtonText: string;
  onSecondaryClick: () => void;
  details: LessonDetail[];
  isLoading?: boolean;
  isReviewEnabled?: boolean; // NEW: Feature flag for showing the review card
}

export const LessonCompletion = ({
  title,
  description,
  reviewTitle,
  reviewDescription,
  primaryButtonText,
  onPrimaryClick,
  secondaryButtonText,
  onSecondaryClick,
  details,
  isLoading = false,
  isReviewEnabled = false,
}: LessonCompletionProps) => {
  return (
    <StyledContainer>
      <StyledSuccessContainer>
        <StyledIconWrapper>
          <Icon
            name="check_circle"
            color="--md-sys-color-secondary"
            size="xxxxxl"
            iconFill
          />
        </StyledIconWrapper>
        <Spacing marginBottom="md">
          <Typography variant="textHeadlineSmallSurface" textAlign="center">
            {title}
          </Typography>
        </Spacing>
        <Typography variant="textBodyMediumOnSurfaceVariant" textAlign="center">
          {description}
        </Typography>
      </StyledSuccessContainer>

      {isReviewEnabled && (
        <>
          <Spacing marginBottom="xl" />
          <StyledReviewCard>
            <StyledReviewHeader>
              <Icon
                name="auto_awesome"
                color="--md-sys-color-primary"
                size="normal"
              />
            </StyledReviewHeader>
            <Spacing marginBottom="sm">
              <Typography variant="textTitleLargeOnSurface" textAlign="center">
                {reviewTitle}
              </Typography>
            </Spacing>
            <Spacing marginBottom="xl">
              <Typography
                variant="textBodyMediumOnSurfaceVariant"
                textAlign="center"
              >
                {reviewDescription}
              </Typography>
            </Spacing>

            <StyledDiv flexDirection="column" gap={16} width="100%">
              <Button
                onClick={onPrimaryClick}
                icon="rate_review"
                loading={isLoading}
                width="100%"
              >
                {primaryButtonText}
              </Button>
              <Button variant="text" onClick={onSecondaryClick} width="100%">
                {secondaryButtonText}
              </Button>
            </StyledDiv>
          </StyledReviewCard>
        </>
      )}

      <Spacing marginBottom="xl" />

      <StyledDetailsList>
        {details.map((detail, index) => (
          <StyledDetailItem key={index}>
            <Icon
              name={detail.icon}
              color="--md-sys-color-primary"
              size="normal"
            />
            <Typography variant="textTitleMediumPrimary">
              {detail.label}
            </Typography>
          </StyledDetailItem>
        ))}
      </StyledDetailsList>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--rk-spacing-xl);
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;

  ${mq(BreakpointSizes.desktop)} {
    padding: var(--rk-spacing-xxl);
  }
`;

const StyledSuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: var(--rk-spacing-xxl);
`;

const StyledIconWrapper = styled.div`
  background-color: var(--md-sys-color-secondary-container);
  border-radius: 50%;
  width: 96px;
  height: 96px;
  margin-bottom: var(--rk-spacing-lg);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledReviewCard = styled.div`
  background-color: var(--md-sys-color-surface-container-low);
  border-radius: 24px;
  padding: 32px 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
`;

const StyledReviewHeader = styled.div`
  margin-bottom: var(--rk-spacing-md);
`;

const StyledDetailsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-lg);
  width: 100%;
  padding-bottom: var(--rk-spacing-xxl);
`;

const StyledDetailItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--rk-spacing-md);
`;
